import { useForm } from '@tanstack/react-form';
import { Button } from '@sara/ui/components/button';
import { Field, FieldContent, FieldError, FieldLabel } from '@sara/ui/components/field';
import { Input } from '@sara/ui/components/input';
import { createApiKeyFormSchema, type CreateApiKeyFormValues } from './schemas';

interface CreateApiKeyFormProps {
  onSubmit: (values: CreateApiKeyFormValues) => void;
  onCancel: () => void;
  isPending: boolean;
  error?: string | null;
}

export function CreateApiKeyForm({ onSubmit, onCancel, isPending, error }: CreateApiKeyFormProps) {
  const form = useForm({
    defaultValues: { label: '' },
    validators: { onSubmit: createApiKeyFormSchema },
    onSubmit: ({ value }) => onSubmit(value),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
      className="space-y-5"
      autoComplete="off"
    >
      <form.Field name="label">
        {(field) => {
          const hasError = field.state.meta.errors.length > 0;
          return (
            <Field data-invalid={hasError || undefined}>
              <FieldLabel htmlFor={field.name} className="text-[13px] font-medium text-foreground">
                Label
              </FieldLabel>
              <FieldContent>
                <Input
                  id={field.name}
                  placeholder="e.g. Jordan — staging testing"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  disabled={isPending}
                  aria-invalid={hasError}
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  Helps you identify this key later. Only used in the console.
                </p>
                <FieldError errors={field.state.meta.errors} />
              </FieldContent>
            </Field>
          );
        }}
      </form.Field>

      {error && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/8 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          shape="pill"
          size="sm"
          onClick={onCancel}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="tinted"
          shape="pill"
          size="sm"
          className="gap-1.5"
          disabled={isPending}
        >
          {isPending ? 'Creating…' : 'Create API key'}
        </Button>
      </div>
    </form>
  );
}
